import * as Api from '@ViewModels';
import { StyleSheet, css } from 'aphrodite';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
	IVolunteerRole,
	IVolunteerShiftRole,
	useCreateVolunteerRoleMutation,
	useVolunteerRolesQuery,
} from '../../../queries/Volunteer';
import { Button } from '../../components-v2/Button';
import { Input } from '../../components-v2/Input';
import { Pill } from '../../components-v2/Pill';
import { Popover, PopoverAnchor, PopoverContent } from '../../components-v2/Popover';
import { THEME } from '../../components-v2/Theme/theme';

interface IProps {
	value: IVolunteerShiftRole[];
	onChange: (roles: IVolunteerShiftRole[]) => void;
	impersonationContext?: Api.IImpersonationContext;
}

export const JobPicker = ({ value, onChange, impersonationContext }: IProps) => {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState('');
	const { data: roles = [] } = useVolunteerRolesQuery({ includeArchived: true, impersonationContext });

	const createMutation = useCreateVolunteerRoleMutation({
		onSuccess: created => {
			onChange([...value, { roleId: created.id, capacity: undefined }]);
			setSearch('');
		},
		onError: error => toast.error(error.systemMessage || 'Failed to create the job'),
	});

	const rolesById = useMemo(() => new Map(roles.map(role => [role.id, role])), [roles]);
	const selected = value
		.filter(entry => rolesById.has(entry.roleId))
		.map(entry => ({ role: rolesById.get(entry.roleId) as IVolunteerRole, capacity: entry.capacity }));

	const selectableRoles = roles.filter(role => !role.isArchived);
	const filtered = selectableRoles.filter(role => role.name.toLowerCase().includes(search.trim().toLowerCase()));
	const exactMatch = selectableRoles.some(role => role.name.toLowerCase() === search.trim().toLowerCase());
	const canCreate = !!search.trim() && !exactMatch;
	const isSelected = (roleId: string) => value.some(entry => entry.roleId === roleId);

	const toggle = (roleId: string) => {
		onChange(
			isSelected(roleId) ? value.filter(entry => entry.roleId !== roleId) : [...value, { roleId, capacity: undefined }]
		);
	};

	const setRoleCapacity = (roleId: string, raw: string) => {
		const trimmed = raw.trim();
		const parsed = trimmed ? Number(trimmed) : undefined;
		const capacity = parsed !== undefined && !Number.isNaN(parsed) ? parsed : undefined;
		onChange(value.map(entry => (entry.roleId === roleId ? { ...entry, capacity } : entry)));
	};

	return (
		<div className={css(styles.root)}>
			{selected.length > 0 ? (
				<div className={css(styles.selectedList)}>
					{selected.map(({ role, capacity }) => (
						<div key={role.id} className={css(styles.selectedRow)}>
							<Pill
								variant='tag'
								color='blue'
								label={role.isArchived ? `${role.name} (archived)` : role.name}
								onRemove={() => toggle(role.id)}
							/>
							<Input
								type='number'
								min={1}
								value={capacity != null ? String(capacity) : ''}
								onChange={e => setRoleCapacity(role.id, e.target.value)}
								placeholder='Unlimited'
								styles={[styles.capacityInput]}
							/>
							<span className={css(styles.spots)}>spots</span>
						</div>
					))}
				</div>
			) : null}

			<Popover open={open} onOpenChange={setOpen}>
				<PopoverAnchor>
					<Button type='button' variant='secondary' size='sm' onClick={() => setOpen(true)}>
						Add job
					</Button>
				</PopoverAnchor>
				<PopoverContent side='bottom' align='start'>
					<div className={css(styles.panel)}>
						<Input
							autoFocus
							value={search}
							onChange={e => setSearch(e.target.value)}
							placeholder='Search or create a job'
						/>
						<div className={css(styles.list)}>
							{filtered.map(role => (
								<Button
									key={role.id}
									type='button'
									variant='ghost'
									size='sm'
									styles={[styles.optionButton]}
									onClick={() => toggle(role.id)}
								>
									<span className={css(isSelected(role.id) && styles.selectedIndicator)}>✓</span>
									{role.name}
								</Button>
							))}
							{canCreate ? (
								<Button
									type='button'
									variant='ghost'
									size='sm'
									styles={[styles.optionButton]}
									disabled={createMutation.isPending}
									onClick={() => createMutation.mutate({ role: { name: search.trim() }, impersonationContext })}
								>
									Create "{search.trim()}"
								</Button>
							) : null}
						</div>
					</div>
				</PopoverContent>
			</Popover>
		</div>
	);
};

const styles = StyleSheet.create({
	root: { display: 'flex', flexDirection: 'column', gap: THEME.spacing.sm },
	selectedList: { display: 'flex', flexDirection: 'column', gap: THEME.spacing.xs },
	selectedRow: { alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: THEME.spacing.sm },
	capacityInput: { maxWidth: 120 },
	spots: { color: THEME.colors.text.light, fontSize: THEME.typography.fontSizes.sm },
	panel: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		minWidth: 240,
		padding: THEME.spacing.sm,
	},
	list: { display: 'flex', flexDirection: 'column', gap: THEME.spacing.xs, maxHeight: 220, overflowY: 'auto' },
	optionButton: { justifyContent: 'flex-start', width: '100%' },
	selectedIndicator: { color: THEME.colors.primary.main },
});
